/**
 * マルチフォーマットエンコーダアダプタ (EncoderAdapter)
 * aidlc-docs/construction/media_export/docs/encoder_adapter_component.md 準拠
 */

import type { ExportProgressDTO, ExportRequestDTO, ExportResultDTO } from '../ports/types';
import { GifEncoder } from './GifEncoder';
import { MediaRecorderEncoder } from './MediaRecorderEncoder';

export interface EncoderAdapterOptions {
  gifEncoder?: GifEncoder;
  videoEncoder?: MediaRecorderEncoder;
  createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;
  recorderFactory?: (stream: any, options: any) => any;
}

export class EncoderAdapter {
  private gifEncoder: GifEncoder;
  private videoEncoder: MediaRecorderEncoder;
  private isCancelled: boolean = false;
  private createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;
  private recorderFactory?: (stream: any, options: any) => any;

  constructor(options: EncoderAdapterOptions = {}) {
    this.gifEncoder = options.gifEncoder || new GifEncoder();
    this.videoEncoder = options.videoEncoder || new MediaRecorderEncoder();
    this.createCanvasFn = options.createCanvasFn;
    this.recorderFactory = options.recorderFactory;
  }

  /**
   * 合成済みフレーム列を目的のフォーマットにエンコード
   */
  public async encode(
    dto: ExportRequestDTO,
    frames: ImageData[],
    onProgress?: (progress: ExportProgressDTO) => void
  ): Promise<ExportResultDTO> {
    if (frames.length === 0) {
      throw new Error('Cannot encode empty frame sequence.');
    }

    this.isCancelled = false;
    const format = dto.format;
    const fps = dto.fps || 30;

    let blob: Blob;
    let mimeType: string;

    const progressCallback = (progress: number) => {
      onProgress?.({
        stage: 'encoding',
        progress: Math.min(1.0, Math.max(0.0, progress)),
      });
    };

    if (format === 'gif') {
      // 𝕏/TwitterのGIF容量制限(15MB)を安全に下回るため、GIFは15fpsにサンプリングして軽量化
      const targetFps = Math.min(15, fps);
      const step = Math.max(1, Math.round(fps / targetFps));
      const sampledFrames = step > 1 ? frames.filter((_, idx) => idx % step === 0) : frames;
      const result = await this.gifEncoder.encode(sampledFrames, targetFps, progressCallback);
      blob = result.blob;
      mimeType = result.mimeType;
    } else if (format === 'mp4' || format === 'webm') {
      const result = await this.videoEncoder.encode(frames, {
        format,
        fps,
        onProgress: progressCallback,
        createCanvasFn: this.createCanvasFn,
        recorderFactory: this.recorderFactory,
      });
      blob = result.blob;
      mimeType = result.mimeType;
    } else {
      throw new Error(`Unsupported export format: ${format}`);
    }

    if (this.isCancelled) {
      throw new Error('Export cancelled');
    }

    const ext = format === 'gif' ? 'gif' : format === 'mp4' ? 'mp4' : 'webm';
    const fileName = `purin_touch_${Date.now()}.${ext}`;

    return {
      blob,
      mimeType,
      fileName,
    };
  }

  /**
   * エンコード中断
   */
  public cancel(): void {
    this.isCancelled = true;
    this.gifEncoder.cancel();
    this.videoEncoder.cancel();
  }
}
